import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../../api/apiClient';
import { AuthState, LoginRequest, RegisterRequest, User } from '../../types/auth.types';
import { formatAuthErrorFromResponse, isEmailNotVerifiedErrorPayload } from '../../utils/authApiErrors';
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY, storeAuthData } from '../../utils/auth';

// Add a function to get the token from localStorage
const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

// Check if token exists initially
const initialToken = getStoredToken();

const initialState: AuthState = {
  user: null,
  token: initialToken,
  isAuthenticated: !!initialToken,
  loading: false,
  error: null,
  // Add a flag to track if we've already attempted to fetch the user
  userFetchAttempted: false
};

// Modified fetch current user to check if we already tried to fetch
const AUTH_INVALID_REJECT_VALUE = '__AUTH_INVALID__';

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const state = getState() as { auth: AuthState };
    const shouldForceRefresh = options?.force === true;
    
    // Exit early if no token is present
    if (!state.auth.token) {
      console.log('fetchCurrentUser: No token found in state');
      return rejectWithValue('No authentication token found');
    }
    
    // Check if token exists in localStorage to confirm it wasn't cleared
    const localStorageToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!localStorageToken) {
      console.log('fetchCurrentUser: Token missing from localStorage');
      return rejectWithValue('Token missing from localStorage');
    }
    
    // Exit early if we've already tried to fetch the user and no user was found
    if (!shouldForceRefresh && state.auth.userFetchAttempted && !state.auth.user) {
      console.log('fetchCurrentUser: Already attempted to fetch user with no success');
      return rejectWithValue('Already attempted to fetch user');
    }
    
    try {
      console.log('fetchCurrentUser: Attempting to fetch user data');
      const response = await apiClient.get('/auth/me');
      console.log('fetchCurrentUser: Successfully fetched user data', response.data);
      const tokenForStorage = state.auth.token || localStorageToken;
      if (tokenForStorage) {
        storeAuthData(tokenForStorage, response.data);
      }
      return response.data;
    } catch (error: any) {
      console.error('fetchCurrentUser error:', error);
      const responseStatusCode = error.response?.status;
      
      // Only clear token on specific auth errors
      if (responseStatusCode === 401 || responseStatusCode === 403) {
        console.log('fetchCurrentUser: Auth error, clearing token');
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
        return rejectWithValue(AUTH_INVALID_REJECT_VALUE);
      } else {
        // For other errors (network, 500, etc.), don't clear the token
        console.log('fetchCurrentUser: Non-auth error, keeping token');
      }
      
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch user data');
    }
  },
  {
    // Only allow one fetch at a time
    condition: (options, { getState }) => {
      const { auth } = getState() as { auth: AuthState };
      const shouldForceRefresh = options?.force === true;
      // Don't fetch if already loading
      if (auth.loading) {
        console.log('fetchCurrentUser condition: Already loading, skipping fetch');
        return false;
      }
      if (shouldForceRefresh) {
        return true;
      }
      const needsProfileRefresh = !!auth.user && (
        auth.user.completed_searches_count === undefined ||
        auth.user.post_search_survey_completed === undefined
      );
      // Don't fetch if we already have a user and don't need refresh
      if (auth.user && auth.isAuthenticated && !needsProfileRefresh) {
        console.log('fetchCurrentUser condition: User already authenticated, skipping fetch');
        return false;
      }
      // Don't fetch again if we've already tried and failed
      if (auth.userFetchAttempted && !auth.user) {
        console.log('fetchCurrentUser condition: Already attempted to fetch user, skipping fetch');
        return false;
      }
      return true;
    }
  }
);

// Rename the thunks to avoid naming conflicts
export const loginThunk = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      // Create form data
      const formData = new URLSearchParams();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);
      
      console.log('loginThunk: Sending login request');
      const tokenResponse = await apiClient.post('/auth/token', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      const token = tokenResponse.data.access_token;
      if (!token) {
        console.error('loginThunk: No token received');
        throw new Error('Authentication failed: No token received');
      }
      
      console.log('loginThunk: Token received, storing and fetching user');
      // Store token immediately so subsequent requests are authenticated
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      
      // Fetch user data using the new token
      // The apiClient's interceptor should automatically use the new token
      try {
        const userResponse = await apiClient.get('/auth/me');
        const user = userResponse.data;
        
        if (!user) {
          console.error('loginThunk: Failed to fetch user data after login');
          // Clean up the stored token if user fetch fails
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          throw new Error('Authentication failed: Could not fetch user data');
        }
        
        console.log('loginThunk: User data fetched successfully');
        // Store both user and token
        storeAuthData(token, user);
        
        return { token, user }; // Return both token and user
      } catch (fetchError: any) {
        // Handle 403 error specifically (email not confirmed)
        if (fetchError.response?.status === 403) {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          const errorDetail = fetchError.response?.data?.detail || 'Please confirm your email address before logging in.';
          throw new Error(errorDetail);
        }
        throw fetchError;
      }
      
    } catch (error: any) {
      console.error('loginThunk Error:', error);
      // Ensure token is cleared on failure
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      // Handle EMAIL_NOT_VERIFIED at the token endpoint (403 with structured detail)
      const detail = error.response?.data?.detail;
      if (isEmailNotVerifiedErrorPayload(detail)) {
        return rejectWithValue(detail);
      }
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed';
      return rejectWithValue(errorMessage);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: RegisterRequest, { rejectWithValue }) => {
    try {
      console.log('register: Sending registration request');
      const response = await apiClient.post('/auth/register', userData);
      
      console.log('register: Registration successful, confirmation email sent');
      return response.data;
      
    } catch (error: any) {
      console.error('register Error:', error);
      return rejectWithValue(formatAuthErrorFromResponse(error));
    }
  }
);

export const logoutThunk = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await apiClient.post('/logout');
      // Always remove token upon logout
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return null;
    } catch (error: any) {
      // Even if logout API fails, remove token
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const googleLoginThunk = createAsyncThunk(
  'auth/googleLogin',
  async (
    payload: { idToken: string; trial_token?: string },
    { rejectWithValue }
  ) => {
    try {
      console.log('googleLoginThunk: Sending Google login request');
      const body: { token: string; trial_token?: string } = {
        token: payload.idToken,
      };
      if (payload.trial_token) {
        body.trial_token = payload.trial_token;
      }
      const tokenResponse = await apiClient.post('/auth/google', body);
      
      const token = tokenResponse.data.access_token;
      if (!token) {
        console.error('googleLoginThunk: No token received');
        throw new Error('Authentication failed: No token received');
      }
      
      console.log('googleLoginThunk: Token received, storing and fetching user');
      // Store token immediately so subsequent requests are authenticated
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      
      // Fetch user data using the new token
      const userResponse = await apiClient.get('/auth/me');
      const user = userResponse.data;
      
      if (!user) {
        console.error('googleLoginThunk: Failed to fetch user data after login');
        // Clean up the stored token if user fetch fails
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        throw new Error('Authentication failed: Could not fetch user data');
      }
      
      console.log('googleLoginThunk: User data fetched successfully');
      // Store both user and token
      storeAuthData(token, user);
      
      return { token, user };
      
    } catch (error: any) {
      console.error('googleLoginThunk Error:', error);
      // Ensure token is cleared on failure
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      return rejectWithValue(formatAuthErrorFromResponse(error));
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Add a reducer to reset user fetch attempt flag
    resetUserFetchAttempt: (state) => {
      state.userFetchAttempted = false;
    },
    login: (state, action: PayloadAction<{ token: string; user: User }>) => {
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.user = action.payload.user;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setOnboardingCompleted: (state) => {
      if (state.user) {
        state.user.onboarding_completed = true;
      }
    },
    setPostSearchSurveyCompleted: (state) => {
      if (state.user) {
        state.user.post_search_survey_completed = true;
      }
    },
  },
  extraReducers: (builder) => {
    // Login - update references to use loginThunk
    builder.addCase(loginThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginThunk.fulfilled, (state, action: PayloadAction<{ token: string; user: User }>) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.userFetchAttempted = true;
      state.error = null;
    });
    builder.addCase(loginThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.isAuthenticated = false;
    });
    
    // Register
    builder.addCase(register.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(register.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(register.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as AuthState['error'];
      state.isAuthenticated = false;
    });
    
    // Logout - update references to use logoutThunk
    builder.addCase(logoutThunk.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(logoutThunk.fulfilled, (state) => {
      state.loading = false;
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.userFetchAttempted = false;
    });
    builder.addCase(logoutThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.userFetchAttempted = false;
    });
    
    // Fetch current user
    builder.addCase(fetchCurrentUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCurrentUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      state.isAuthenticated = true;
      state.userFetchAttempted = true;
    });
    builder.addCase(fetchCurrentUser.rejected, (state, action) => {
      state.loading = false;
      const rejectedPayload = action.payload as string | undefined;
      if (rejectedPayload === AUTH_INVALID_REJECT_VALUE) {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = 'Session expired. Please log in again.';
      } else {
        state.error = rejectedPayload || 'Failed to fetch user data';
      }
      state.userFetchAttempted = true;
    });
    
    // Google Login
    builder.addCase(googleLoginThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(googleLoginThunk.fulfilled, (state, action: PayloadAction<{ token: string; user: User }>) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.userFetchAttempted = true;
      state.error = null;
    });
    builder.addCase(googleLoginThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as AuthState['error'];
      state.isAuthenticated = false;
    });
  }
});

// Export the actions without conflicts
export const { clearError, resetUserFetchAttempt, login, logout, setUser, setOnboardingCompleted, setPostSearchSurveyCompleted } = authSlice.actions;

export default authSlice.reducer; 